import type React from "react"
import { useTranslation } from "react-i18next"

import { Decimal } from "@/components/decimal/decimal"
import type { Site } from "@/models/site"
import { UnitName } from "@/models/unit"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { getDisplayValue } from "@/utils/value"

import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material"
import Grid from "@mui/material/Grid"

import { SiteMap } from "../map/siteMap"
import { getSiteAddress } from "./siteInfoUtils"

interface SiteInfoContentProps {
  onClose: () => void
  site: Site | null
  /**
   * Optional content to display below the primary site info
   */
  supplementalInfo?: React.ReactNode
}

export const SiteInfoContent: React.FC<SiteInfoContentProps> = ({
  onClose,
  site,
  supplementalInfo,
}) => {
  const { t } = useTranslation("sites")

  const siteAddress = getSiteAddress(site?.address)
  return (
    <Stack
      aria-label={`${site?.name} info`}
      component="section"
      sx={{
        gap: 2,
        height: "100%",
        p: 2,
        pt: 0,
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          pt: 2,
          bgcolor: (theme) => theme.palette.background.paper,
          position: "sticky",
          top: 0,

          // Above the Google map
          zIndex: 4,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            pb: 2,
          }}
        >
          <Typography variant="h2">{site?.name}</Typography>
          <IconButton
            aria-label={t("button.close", { ns: "common" })}
            onClick={onClose}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Divider />
      </Box>
      <Box>
        <Box
          sx={{
            py: 2,
          }}
        >
          <Grid container spacing={1}>
            <Grid size={{ xs: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "fontWeightBold",
                }}
              >
                {t("site.info.location")}
              </Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
              {site?.address ? (
                <Stack direction="column">
                  <Typography variant="body2">
                    {site.address.address_line_1}
                    {site.address.address_line_2
                      ? ` ${site.address.address_line_2}`
                      : ""}
                    ,
                  </Typography>
                  <Typography variant="body2">
                    {site.address.city}, {site.address.state}{" "}
                    {site.address.postal_code}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2">
                  {EMPTY_DATA_PLACEHOLDER}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {t("fields.group.title", { ns: "common" })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Typography variant="body2">
                {getDisplayValue(site?.departmentName)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider />
      </Box>
      <SiteMap address={siteAddress ?? ""} />
      <Divider />
      <Box>
        <Grid container spacing={1}>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "fontWeightBold",
              }}
            >
              {t("fields.buildingType.title", { ns: "common" })}
            </Typography>
          </Grid>
          <Grid size={{ xs: 8 }}>
            <Typography variant="body2">
              {getDisplayValue(site?.buildingType)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "fontWeightBold",
              }}
            >
              {t("fields.buildingArea.title", { ns: "common" })}
            </Typography>
          </Grid>
          <Grid size={{ xs: 8 }}>
            <Typography variant="body2">
              <Decimal
                format={{ precision: 1 }}
                unit={UnitName.SquareFoot}
                value={site?.sqFt}
              />
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "fontWeightBold",
              }}
            >
              {t("site.info.yearBuilt")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 8 }}>
            <Typography variant="body2">
              {getDisplayValue(site?.yearBuilt)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {supplementalInfo ? (
        <>
          <Divider />
          {supplementalInfo}
        </>
      ) : null}
    </Stack>
  )
}
